import { AlertTriangle, Phone } from "lucide-react";

interface UkHealthDisclaimerProps {
  compact?: boolean;
  variant?: "patient" | "nurse";
}

export function UkHealthDisclaimer({ compact = false, variant = "patient" }: UkHealthDisclaimerProps) {
  if (variant === "nurse") {
    if (compact) {
      return (
        <div className="flex items-start gap-3 bg-teal-50 border border-teal-200 rounded-2xl px-5 py-4 text-sm text-teal-900">
          <AlertTriangle className="h-4 w-4 text-teal-600 shrink-0 mt-0.5" />
          <p>
            This content is for clinical reference only. Always apply your professional judgment and refer to current NICE/BSR guidelines. You remain accountable under the NMC Code.
          </p>
        </div>
      );
    }

    return (
      <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="bg-teal-100 p-2.5 rounded-2xl shrink-0">
            <AlertTriangle className="h-5 w-5 text-teal-600" />
          </div>
          <div className="space-y-2">
            <h3 className="font-bold text-teal-900">Clinical Reference Information</h3>
            <p className="text-teal-800 text-sm leading-relaxed">
              This content is for <strong>clinical reference and educational purposes only</strong>. Always apply your professional judgment, verify against current NICE, BSR, and EULAR guidelines, and consult your supervising consultant where appropriate. You remain accountable under the <strong>NMC Code</strong> for all clinical decisions.
            </p>
            <p className="text-xs text-teal-700">
              Content is aligned with MHRA guidance and UK regulatory standards. This platform does not replace clinical protocols, trust policies, or prescribing authority.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 text-sm text-amber-900">
        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <p>
          This information is for educational purposes only and does not replace advice from your doctor, nurse, or pharmacist.
          If you have concerns, contact your healthcare team or call{" "}
          <a href="tel:111" className="font-semibold underline">NHS 111</a>.
          In an emergency, call <a href="tel:999" className="font-semibold underline">999</a>.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 sm:p-8">
      <div className="flex items-start gap-4">
        <div className="bg-amber-100 p-2.5 rounded-2xl shrink-0">
          <AlertTriangle className="h-6 w-6 text-amber-600" />
        </div>
        <div className="space-y-3">
          <h3 className="font-bold text-amber-900 text-lg">Important Health Information</h3>
          <p className="text-amber-800 leading-relaxed">
            This information is for <strong>educational purposes only</strong> and does not replace personalised advice from your doctor, specialist nurse, or pharmacist. Every person's situation is different — always speak to your healthcare team about your own care and treatment.
          </p>
          <div className="flex flex-wrap gap-3 pt-1">
            <a
              href="tel:111"
              className="inline-flex items-center gap-2 bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold text-sm px-4 py-2 rounded-full border border-amber-300 transition-colors"
            >
              <Phone className="h-4 w-4" />
              Call NHS 111
            </a>
            <a
              href="tel:999"
              className="inline-flex items-center gap-2 bg-rose-100 hover:bg-rose-200 text-rose-900 font-semibold text-sm px-4 py-2 rounded-full border border-rose-300 transition-colors"
            >
              <Phone className="h-4 w-4" />
              Emergency: 999
            </a>
          </div>
          <p className="text-xs text-amber-700">
            Medicines information complies with MHRA guidelines. This site does not provide medical diagnoses or personalised prescribing advice.
          </p>
        </div>
      </div>
    </div>
  );
}
